import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormArray } from '@angular/forms';

// PrimeNG
import { TabViewModule } from 'primeng/tabview';
import { ButtonModule } from 'primeng/button';

// Custom / Shared components
import { UserMenuComponent } from '../../shared/user-menu/user-menu.component';
import { InputDisplayGridComponent } from '../../shared/input-display-grid/input-display-grid.component';
import { DisplayImagesComponent } from '../../shared/display-images/display-images.component';
import { IncidentReportStep1Component } from '../incident-report-stepper/incident-report-step1/incident-report-step1.component';
import { IncidentReportStep2Component } from '../incident-report-stepper/incident-report-step2/incident-report-step2.component';

// Models
import { EodIncidentReport } from '../../model/incident-report/eod-incident-report.model';
import { getHeroName, HeroReport } from '../../model/hero-report/hero-report.model';

// Services
import { IncidentReportService } from '../../model/incident-report/incident-report.service';
import { HeroReportService } from '../../model/hero-report/hero-report.service';
import { PermissionService } from '../../permissions.service';

import { UTMConversionService } from '../../model/utm-conversion/utm-conversion.service';

import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AccountService } from '../../model/account/account.service';
import { Account } from '../../model/account/account.model';
import { HeroReportImageStorageService } from '../../model/hero-report-image/hero-report-image-storage.service';

function arrivalAfterDepartureValidator(): ValidatorFn {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const departure = formGroup.get('departureDate')?.value;
    const arrival = formGroup.get('arrivalDate')?.value;

    // If either is missing, we don't validate yet
    if (!departure || !arrival) {
      return null;
    }

    // Compare: if arrival is NOT strictly after departure, return an error
    if (new Date(arrival) <= new Date(departure)) {
      return { arrivalBeforeDeparture: true };
    }

    return null;
  };
}

/** For <app-input-display-grid> */
interface DisplayValue {
  label: string;
  value: string;
}

@Component({
  selector: 'app-incident-report',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,

    // PrimeNG
    TabViewModule,
    ButtonModule,

    // Custom / Shared
    UserMenuComponent,
    InputDisplayGridComponent,
    DisplayImagesComponent,
    IncidentReportStep1Component,
    IncidentReportStep2Component,
  ],
  templateUrl: './incident-report.component.html',
  styleUrls: ['./incident-report.component.scss'],
})
export class IncidentReportComponent implements OnInit {
  @Input() heroReportId: string | null = null;
  @Input() incidentId: string | null = null;

  incidentReport: EodIncidentReport = {} as EodIncidentReport;
  heroReport: HeroReport = {} as HeroReport;

  // For read-only <app-input-display-grid>
  initialInformation: DisplayValue[] = [];
  actionInformation: DisplayValue[] = [];
  dimensions: DisplayValue[] = [];
  images: { label: string; value: string[] }[] = [];

  // Tab & Edit states
  activeTabIndex = 0;
  currentlyEditingTab: number | null = null;

  // Two forms
  initialForm!: FormGroup;
  actionForm!: FormGroup;

  // Permissions to show/hide action buttons
  canEditEod = false;
  canDeleteEod = false;

  // For <app-input-select> (Incident Type dropdown)
  incidentTypeOptions = [
    { label: $localize`:@@incidentType.routine:Routine`, value: 'ROUTINE' },
    { label: $localize`:@@incidentType.unusual:Unusual`, value: 'UNUSUAL' },
  ];

  account: Account;

  // Object URLs for the images already downloaded from S3, by image id.
  private imageUrls = new Map<string, string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private incidentReportService: IncidentReportService,
    private heroReportService: HeroReportService,
    private utmConversionService: UTMConversionService,
    private permissionService: PermissionService,
    private accountService: AccountService,
    private imageStorage: HeroReportImageStorageService
  ) { }

  async ngOnInit(): Promise<void> {
    this.buildForms();

    this.canEditEod = this.permissionService.can('edit:eod');
    this.canDeleteEod = this.permissionService.can('delete:eod');

    this.account = await this.accountService.fetch();

    // Grab route params
    this.route.params.subscribe(async (params) => {
      const heroReportId = params['heroReportId'];
      const incidentId = params['id'];

      if (heroReportId) this.heroReportId = heroReportId;
      if (incidentId) this.incidentId = incidentId;

      if (this.heroReportId && this.incidentId) {
        try {
          // Load data
          this.incidentReport = await this.incidentReportService.fetchById(
            this.incidentId
          );
          this.heroReport = await this.heroReportService.fetchById(
            this.heroReportId
          );

          // Populate forms & read-only data
          this.populateForms();
          this.setReadOnlyData();
          this.loadImageUrls();
        } catch (error) {
          console.error('Error loading data:', error);
        }
      }
    });
  }

  /** Build two forms: one for “Initial Information,” one for “Action by EOD.” */
  buildForms(): void {
    const heroReportId = this.route.snapshot.paramMap.get('heroReportId');

    this.heroReportService.fetchById(heroReportId).then((heroReport) => {
      this.heroReport = heroReport;
    });

    this.initialForm = this.fb.group({
      eodIncidentNumber: [null],
      incidentType: [null],
    });

    this.actionForm = this.fb.group(
      {
        eodTeamDispatched: [''],
        departureDate: [null],
        arrivalDate: [null],
        narrative: [''],
        disposition: [null],
        completedDate: [null],
        images: [[]],
        widthCm: [null],
        lengthCm: [null],
        heightCm: [null],
        diameterCm: [null],
      },
      {
        validators: [
          (group: FormGroup) => {
            const departure = group.get('departureDate')?.value;
            const arrival = group.get('arrivalDate')?.value;
            const completed = group.get('completedDate')?.value;

            const errors: any = {};

            if (departure && arrival && new Date(arrival) <= new Date(departure)) {
              errors.arrivalBeforeDeparture = true;
            }
            if (arrival && completed && new Date(completed) <= new Date(arrival)) {
              errors.completedBeforeArrival = true;
            }

            return Object.keys(errors).length ? errors : null;
          }
        ]
      }
    );
  }

  /** Fill forms with existing data from incidentReport. */
  populateForms(): void {
    // INITIAL FORM
    this.initialForm.patchValue({
      eodIncidentNumber: this.incidentReport.eodIncidentNumber || '',
      incidentType: this.incidentReport.incidentType,
    });

    // ACTION FORM
    this.actionForm.patchValue({
      eodTeamDispatched: this.incidentReport.eodTeamDispatched || '',
      departureDate: this.incidentReport.departureDate || null,
      arrivalDate: this.incidentReport.arrivalDate || null,
      narrative: this.incidentReport.narrative || '',
      disposition: this.incidentReport.disposition,
      completedDate: this.incidentReport.completedDate || null,

      widthCm: this.incidentReport.widthCm,
      lengthCm: this.incidentReport.lengthCm,
      heightCm: this.incidentReport.heightCm,
      diameterCm: this.incidentReport.diameterCm,
    });
  }

  setReadOnlyData() {
    const coordinates = this.utmConversionService.useOrConvertCoordinates(
      this.heroReport.latitude,
      this.heroReport.longitude,
      this.account.gpsUnit
    );

    this.initialInformation = [
      {
        label: $localize`:@@incidentReport.eodIncidentNumber:EOD Incident Number`,
        value: this.incidentReport.eodIncidentNumber || $localize`:@@common.notAvailable:N/A`,
      },
      {
        label: $localize`:@@incidentReport.incidentType:Incident Type`,
        value: this.incidentReport.incidentType || $localize`:@@common.notAvailable:N/A`,
      },
      {
        label: $localize`:@@incidentReport.dateTimeReport:Date and Time Report`,
        value: this.heroReport.createdDate
          ? new Date(this.heroReport.createdDate).toLocaleString($localize.locale, {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })
          : $localize`:@@common.unknown:Unknown`,
      },
      {
        label: $localize`:@@incidentReport.reportedBy:Reported By`,
        value: getHeroName(this.heroReport),
      },
      {
        label: $localize`:@@incidentReport.incidentLocation:Incident Location`,
        value: coordinates,
      },
    ];

    // 2) "Action by EOD"
    this.actionInformation = [
      {
        label: $localize`:@@incidentReport.eodTeamDispatched:EOD Team Dispatched`,
        value: this.incidentReport.eodTeamDispatched || $localize`:@@common.notAvailable:N/A`,
      },
      {
        label: $localize`:@@incidentReport.departureDatetime:Departure Date and Time`,
        value: this.incidentReport.departureDate
          ? new Date(this.incidentReport.departureDate).toLocaleString($localize.locale, {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })
          : '-',
      },
      {
        label: $localize`:@@incidentReport.arrivalDatetime:Arrival Date and Time`,
        value: this.incidentReport.arrivalDate
          ? new Date(this.incidentReport.arrivalDate).toLocaleString($localize.locale, {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })
          : '-',
      },
      {
        label: $localize`:@@incidentReport.narrative:Incident Narrative`,
        value: this.incidentReport.narrative || '-',
      },
      {
        label: $localize`:@@incidentReport.disposition:Disposition`,
        value: this.incidentReport.disposition || '-',
      },
      {
        label: $localize`:@@incidentReport.completedDatetime:Completed Date and Time`,
        value: this.incidentReport.completedDate
          ? new Date(this.incidentReport.completedDate).toLocaleString($localize.locale, {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })
          : '-',
      },
    ];

    // 3) "Dimensions"
    this.dimensions = [
      {
        label: $localize`:@@dimensions.width:Width (mm)`,
        value: this.incidentReport.widthCm != null
          ? this.incidentReport.widthCm.toString() : '',
      },
      {
        label: $localize`:@@dimensions.length:Length (mm)`,
        value: this.incidentReport.lengthCm != null
          ? this.incidentReport.lengthCm.toString() : '',
      },
      {
        label: $localize`:@@dimensions.height:Height (mm)`,
        value: this.incidentReport.heightCm != null
          ? this.incidentReport.heightCm.toString() : '',
      },
      {
        label: $localize`:@@dimensions.diameter:Diameter (mm)`,
        value: this.incidentReport.diameterCm != null
          ? this.incidentReport.diameterCm.toString() : '',
      },
    ];

    // 4) EOD Images from heroReport
    this.buildImageGroups();
  }

  /** Downloads the EOD images from S3, then refreshes the image groups. */
  private async loadImageUrls(): Promise<void> {
    await Promise.all(
      (this.heroReport.heroReportImages || [])
        .filter((image) => image.type === 'EOD' && image.id && image.s3ObjectKey)
        .map(async (image) => {
          const url = await this.imageStorage.getObjectUrl(image);
          if (url) {
            this.imageUrls.set(image.id, url);
          }
        })
    );
    this.buildImageGroups();
  }

  /** The EOD image group, from the images downloaded so far. */
  private buildImageGroups(): void {
    this.images = [];
    if (this.heroReport.heroReportImages?.length > 0) {
      const groupedMap = this.heroReport.heroReportImages.reduce(
        (acc, image) => {
          if (image.type !== 'EOD') return acc; // skip non-EOD
          const url = this.imageUrls.get(image.id);
          if (!url) return acc;
          const label = $localize`:@@incidentReport.supportingImages:Supporting Images`;
          if (!acc[label]) {
            acc[label] = [];
          }
          acc[label].push(url);
          return acc;
        },
        {} as Record<string, string[]>
      );

      this.images = Object.keys(groupedMap).map((label) => ({
        label,
        value: groupedMap[label],
      }));
    }
  }

  /** Called when user clicks “Edit” in read-only mode. */
  editTab(tabIndex: number): void {
    this.currentlyEditingTab = tabIndex;
  }

  /** Saves data from the tab’s form into incidentReport, then stops editing. */
  saveTab(tabIndex: number): void {
    let form: FormGroup;
    if (tabIndex === 0) form = this.initialForm;
    else form = this.actionForm;

    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    const updated = form.value;

    if (tabIndex === 0) {
      // Merge form data into incidentReport
      this.incidentReport.eodIncidentNumber = updated.eodIncidentNumber;
      this.incidentReport.incidentType = updated.incidentType;
    } else {
      this.incidentReport.eodTeamDispatched = updated.eodTeamDispatched;
      this.incidentReport.departureDate = updated.departureDate || null;
      this.incidentReport.arrivalDate = updated.arrivalDate || null;
      this.incidentReport.narrative = updated.narrative;
      this.incidentReport.disposition = updated.disposition;
      this.incidentReport.completedDate = updated.completedDate || null;

      this.incidentReport.widthCm = updated.widthCm;
      this.incidentReport.lengthCm = updated.lengthCm;
      this.incidentReport.heightCm = updated.heightCm;
      this.incidentReport.diameterCm = updated.diameterCm;
    }

    // Refresh read-only display
    this.setReadOnlyData();
    this.currentlyEditingTab = null;

    this.incidentReportService.update(this.incidentReport);
  }

  /** Revert form data, end edit mode. */
  cancelEdit(tabIndex: number): void {
    this.populateForms();
    this.currentlyEditingTab = null;
  }

  /** Block changing tabs if we’re editing another tab. */
  onTabChange(event: any): void {
    const newIndex = event.index;
    if (
      this.currentlyEditingTab !== null &&
      this.currentlyEditingTab !== newIndex
    ) {
      event.originalEvent.preventDefault();
    } else {
      this.activeTabIndex = newIndex;
    }
  }

  /** Disable a tab if editing a different one. */
  isTabDisabled(index: number): boolean {
    return (
      this.currentlyEditingTab !== null && this.currentlyEditingTab !== index
    );
  }

  goBack(): void {
    this.router.navigate(['/events'], { queryParams: { recenter: 'true' } });
  }

  async deleteIncidentReport(): Promise<void> {
    if (confirm($localize`:@@deleteEodConfirm:Are you sure you want to delete this EOD incident report? This action cannot be undone.`)) {
      try {
        await this.incidentReportService.delete(this.incidentReport.id);
        this.router.navigate(['/events'], { queryParams: { recenter: 'true' } });
      } catch (error) {
        console.error('Failed to delete EOD incident report:', error);
      }
    }
  }

  /** Jump to the associated Hero Report if needed. */
  viewHeroReport(): void {
    if (this.heroReport.id) {
      this.router.navigate(['/hero-report', this.heroReport.id]);
    }
  }
}
